import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Star, Crown, Package, Coins, Camera, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [edit, setEdit] = useState({ name: "", bio: "", city: "", province: "" });
  const [code, setCode] = useState("");

  const load = async () => {
    const [l, r] = await Promise.all([api.get("/listings/mine"), api.get(`/reviews/${user.user_id}`)]);
    setListings(l.data); setReviews(r.data);
  };
  useEffect(() => { if (user) { setEdit({ name: user.name, bio: user.bio || "", city: user.city || "", province: user.province || "" }); load(); } /* eslint-disable-next-line */ }, [user?.user_id]);

  const saveProfile = async () => {
    try { await api.put("/profile", edit); await refresh(); toast.success("Profilo aggiornato"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/profile", { avatar_url: data.url }); await refresh(); toast.success("Foto aggiornata");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const verifyEmail = async () => {
    try { await api.post("/auth/verify-email", { code }); await refresh(); toast.success("Email verificata!"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const verifyIdentity = async () => {
    try { await api.post("/auth/verify-identity"); await refresh(); toast.success("Identità verificata!"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!user) return null;

  const stats = [
    { label: "Crediti", value: user.credits, icon: Coins, accent: true },
    { label: "Valutazione", value: user.rating_avg ? `${user.rating_avg}★` : "—", icon: Star },
    { label: "Donazioni", value: user.donations_count, icon: Package },
    { label: "Recensioni", value: user.rating_count, icon: BadgeCheck },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="rounded-2xl bg-card border border-border/60 tactile-shadow p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="relative">
            <Avatar className="h-20 w-20 ring-4 ring-primary/10"><AvatarImage src={mediaUrl(user.avatar_url)} /><AvatarFallback className="bg-primary/10 text-primary text-xl font-bold">{user.name?.slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
            <label className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-1.5 cursor-pointer" data-testid="avatar-upload-label">
              <Camera className="h-3.5 w-3.5" /><input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-upload-input" />
            </label>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-head font-extrabold text-2xl sm:text-3xl">{user.name}</h1>
              {user.plan === "premium" && <Badge className="bg-[hsl(var(--gold))] text-white border-0"><Crown className="h-3 w-3 mr-1" /> Premium</Badge>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge variant={user.email_verified ? "default" : "outline"} className={user.email_verified ? "bg-success text-white border-0" : ""} data-testid="email-verify-chip">
                <Mail className="h-3 w-3 mr-1" /> {user.email_verified ? "Email verificata" : "Email non verificata"}
              </Badge>
              <Badge variant={user.identity_verified ? "default" : "outline"} className={user.identity_verified ? "bg-success text-white border-0" : ""} data-testid="identity-verify-chip">
                <ShieldCheck className="h-3 w-3 mr-1" /> {user.identity_verified ? "Identità verificata" : "Identità non verificata"}
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {stats.map((s) => (
            <div key={s.label} className={`rounded-2xl p-4 border ${s.accent ? "bg-[hsl(var(--gold))]/10 border-[hsl(var(--gold))]/40" : "bg-secondary border-border/60"}`} data-testid={`stat-${s.label}`}>
              <s.icon className={`h-5 w-5 ${s.accent ? "text-[hsl(var(--gold))]" : "text-primary"}`} />
              <div className="font-head font-extrabold text-2xl mt-2">{s.value}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {(!user.email_verified || !user.identity_verified) && (
        <div className="flex flex-wrap gap-3 mt-6">
          {!user.email_verified && (
            <Dialog>
              <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="verify-email-btn"><Mail className="h-4 w-4 mr-1" /> Verifica email</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Verifica email</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Inserisci il codice ricevuto (demo: mostrato alla registrazione e nelle notifiche).</p>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Codice" className="rounded-xl" data-testid="verify-code-input" />
                <DialogFooter><Button onClick={verifyEmail} className="rounded-full" data-testid="confirm-verify-email">Verifica</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {!user.identity_verified && <Button variant="outline" className="rounded-full" onClick={verifyIdentity} data-testid="verify-identity-btn"><ShieldCheck className="h-4 w-4 mr-1" /> Verifica identità</Button>}
        </div>
      )}

      <Tabs defaultValue="listings" className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="listings" className="rounded-full" data-testid="tab-listings">I miei annunci</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-full" data-testid="tab-reviews">Recensioni</TabsTrigger>
          <TabsTrigger value="edit" className="rounded-full" data-testid="tab-edit">Modifica</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-6">
          {listings.length === 0 ? <p className="text-muted-foreground py-8 text-center">Non hai ancora pubblicato annunci. <Link to="/new" className="text-primary font-semibold">Dona ora</Link></p> : (
            <div className="grid gap-3">
              {listings.map((l) => (
                <Link to={`/item/${l.id}`} key={l.id} className="flex items-center gap-4 rounded-2xl border border-border/60 p-3 hover:bg-secondary transition-colors" data-testid={`my-listing-${l.id}`}>
                  <div className="h-16 w-16 rounded-xl bg-secondary overflow-hidden shrink-0">{l.images?.[0] && <img src={mediaUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}</div>
                  <div className="flex-1 min-w-0"><div className="font-semibold truncate">{l.title}</div><div className="text-xs text-muted-foreground">{l.category} · {l.city}</div></div>
                  <Badge variant="outline" className={
                    l.status === "approved" ? "border-success text-success" :
                    l.status === "pending" ? "border-[hsl(var(--gold))] text-[hsl(30_60%_40%)]" :
                    l.status === "delivered" ? "bg-success text-white border-0" :
                    l.status === "reserved" ? "bg-accent text-white border-0" : "border-destructive text-destructive"
                  }>{ {approved:"Approvato",pending:"In moderazione",delivered:"Consegnato",reserved:"Prenotato",rejected:"Rifiutato"}[l.status] }</Badge>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          {reviews.length === 0 ? <p className="text-muted-foreground py-8 text-center">Ancora nessuna recensione.</p> : (
            <div className="grid gap-3">
              {reviews.map((r) => (
                <div key={r.id} className="rounded-2xl border border-border/60 p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between"><span className="font-semibold">{r.reviewer_name}</span>
                    <span className="flex items-center gap-0.5">{Array.from({length:5}).map((_,i)=><Star key={i} className={`h-4 w-4 ${i<r.rating?"text-[hsl(var(--gold))] fill-[hsl(var(--gold))]":"text-border"}`} />)}</span>
                  </div>
                  {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="edit" className="mt-6">
          <div className="max-w-lg space-y-4">
            <div className="space-y-2"><Label>Nome</Label><Input value={edit.name} onChange={(e)=>setEdit({...edit,name:e.target.value})} className="h-11 rounded-xl" data-testid="edit-name" /></div>
            <div className="space-y-2"><Label>Bio</Label><Textarea value={edit.bio} onChange={(e)=>setEdit({...edit,bio:e.target.value})} className="rounded-xl" data-testid="edit-bio" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Città</Label><Input value={edit.city} onChange={(e)=>setEdit({...edit,city:e.target.value})} className="h-11 rounded-xl" data-testid="edit-city" /></div>
              <div className="space-y-2"><Label>Provincia</Label><Input value={edit.province} onChange={(e)=>setEdit({...edit,province:e.target.value})} className="h-11 rounded-xl" data-testid="edit-province" /></div>
            </div>
            <Button onClick={saveProfile} className="rounded-full" data-testid="save-profile-btn">Salva modifiche</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
